import React, { useEffect, useState } from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import {
  useAddressManagers,
  useAddressMutations,
  useCompanyUsers,
  useSetAddressManagers,
} from 'hooks/api'

import { alpha, useTheme, Stack, Typography, Divider, Checkbox, FormControlLabel } from '@mui/material'
import { BriefcaseIcon, StoreIcon, FactoryIcon, TruckIcon, PlusIcon } from 'lucide-react'

import { Button, Input, Modal, AddressField, type ParsedAddress } from '@repo/ui'
import { InfoTooltip, SelectCountryField, UserSelectField } from 'components'
import type { Address, AddressType } from 'types'
import { getAddressIcon } from 'utils/addressIcons'

import { AddressIconPicker } from './AddressIconPicker'

interface CreateAddressModalProps {
  open: boolean
  onClose: () => void
  refetch: () => void
  // When provided the modal edits an existing address instead of creating one.
  address?: Address | null
}

type FormState = {
  type: AddressType
  label: string
  icon: string | null
  full_name: string
  country: string
  state: string
  city: string
  postal_code: string
  address_line: string
  latitude: number | null
  longitude: number | null
  place_id: string | null
  formatted_address: string | null
  is_default: boolean
  show_in_provenance: boolean
}

const EMPTY: FormState = {
  // VRG-528 — Store, not Office. A store is the address type this product is actually
  // about: it is the only type shown on the public chain of custody by default
  // (`custody-provenance.service.ts`), and it is what most brands add first.
  type: 'store',
  // Sensible default label for a new address (editable). Editing an existing
  // address always loads that address's own label, not this default.
  label: 'Store',
  icon: null,
  full_name: '',
  country: '',
  state: '',
  city: '',
  postal_code: '',
  address_line: '',
  latitude: null,
  longitude: null,
  place_id: null,
  formatted_address: null,
  is_default: false,
  // Derived from `type` above by the same rule the API applies to a NULL
  // (`type === 'store'`), and kept in step by `setType`. See its comment.
  show_in_provenance: true,
}

// Labels mirror `ADDRESS_TYPE_LABELS` and the icons mirror `getAddressIcon`, so a type
// is picked here under the same glyph and word it is rendered under everywhere else.
// 'other' ("Custom") stays last — it is the catch-all, and it is the only chip that
// reveals the icon picker.
const ADDRESS_TYPE_OPTIONS: { value: AddressType; label: string; icon: React.ReactNode }[] = [
  { value: 'office', label: 'Office', icon: <BriefcaseIcon strokeWidth={1} size={20} /> },
  { value: 'store', label: 'Store', icon: <StoreIcon strokeWidth={1} size={20} /> },
  { value: 'production', label: 'Production', icon: <FactoryIcon strokeWidth={1} size={20} /> },
  { value: 'distributor', label: 'Distributor', icon: <TruckIcon strokeWidth={1} size={20} /> },
  { value: 'other', label: 'Custom', icon: <PlusIcon strokeWidth={1} size={20} /> },
]

// Field label + control, matching the Figma stacked layout.
const Field = ({
  label,
  optional,
  adornment,
  children,
}: {
  label: string
  optional?: boolean
  // Inline beside the label — in practice an `InfoTooltip`. Mirrors the prop of the
  // same name on `@repo/ui`'s `FieldLabel` and the platform `TextField`.
  adornment?: React.ReactNode
  children: React.ReactNode
}) => (
  <Stack spacing={2} sx={{ flex: 1 }}>
    <Typography variant="body3">
      {label}
      {optional && (
        <Typography component="span" variant="body4" color="text.secondary">
          {'  '}(optional)
        </Typography>
      )}
      {adornment}
    </Typography>
    {children}
  </Stack>
)

export const CreateAddressModal = (props: CreateAddressModalProps) => {
  const { open, onClose = () => {}, refetch, address } = props
  const theme = useTheme()
  const isEdit = Boolean(address)

  const { open: notification } = useSnackbar()
  const { create, update, isSaving } = useAddressMutations()

  /*
    VRG-565 — location managers.

    The picker only offers company members, and the API only ACCEPTS company members
    (SetAddressManagersUseCase rejects the rest, and `address_managers`' composite
    foreign keys reject them again at the database). The UI filter is convenience; it is
    not the security boundary.

    `limit: 200` overrides the hook's default of 50 — that default is a page size for a
    paginated table, and silently truncating a picker would hide staff from a brand with
    a large team with nothing on screen saying so.
  */
  const { data: companyUsersData, isLoading: isLoadingUsers } = useCompanyUsers({ limit: 200 })
  const { managerUserIds, isLoading: isLoadingManagers } = useAddressManagers(address?.id)
  const { setManagers, isSavingManagers } = useSetAddressManagers()

  const [form, setForm] = useState<FormState>(EMPTY)
  const [selectedManagerIds, setSelectedManagerIds] = useState<Set<string>>(new Set())

  /*
    The picker reports the WHOLE desired set, not a toggle — `SearchSelect` is a
    selection, and the API's `PUT .../managers` is a replace (its own DTO says "send the
    full desired list, not a delta"). Both ends now speak in sets, so nothing in between
    has to reconstruct one from a stream of toggles.
  */
  const setManagerIds = (userIds: string[]) => setSelectedManagerIds(new Set(userIds))

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }))

  /*
    VRG-528 — picking a type while CREATING re-derives the provenance box.

    The API's rule is `show_in_provenance ?? type === 'store'`
    (`custody-provenance.service.ts`), and the edit path above already loads the box that
    way so it "shows what actually happens rather than an unset field". Create did not:
    it hardcoded `false`, which agreed with the old default chip (Office) by coincidence.
    With Store as the default that coincidence breaks, and a brand adding a shop — the
    one type whose whole purpose is to be named on the public chain of custody — would
    silently persist it hidden.

    Re-deriving on every type change rather than seeding the initial value once is the
    only option that is safe in BOTH directions. Seeding `true` would leave the box
    ticked after switching to Production, which publishes a contract manufacturer on a
    page anyone can open — the failure the checkbox's own comment below calls out, and
    much worse than under-publishing a shop. Explicit ticks still win: this only fires
    when the type itself changes, and never on edit, where the stored value is the user's
    own past decision and must not be overwritten by re-picking the same chip.
  */
  const setType = (value: AddressType) =>
    setForm(prev => ({
      ...prev,
      type: value,
      show_in_provenance: isEdit ? prev.show_in_provenance : value === 'store',
    }))

  // Sync the form with the target address whenever the modal opens.
  useEffect(() => {
    if (!open) return
    if (address) {
      setForm({
        type: address.type,
        label: address.label ?? '',
        icon: address.icon ?? null,
        full_name: address.full_name ?? '',
        country: address.country ?? '',
        state: address.state ?? '',
        city: address.city ?? '',
        postal_code: address.postal_code ?? '',
        address_line: address.address_line ?? '',
        latitude: address.latitude ?? null,
        longitude: address.longitude ?? null,
        place_id: address.place_id ?? null,
        formatted_address: address.formatted_address ?? null,
        is_default: address.is_default,
        // NULL in the DB means 'decide by type'; the form shows the EFFECTIVE
        // value so the box reflects what actually happens today.
        show_in_provenance: address.show_in_provenance ?? address.type === 'store',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, address])

  /*
    Managers are seeded separately from the form because they arrive on their own
    request. Keyed on the FETCHED ids rather than on `open`: the modal instance is never
    unmounted (AddressBookPage swaps `address` under it), so seeding on open alone would
    latch whatever the previous address's query had already resolved.
  */
  useEffect(() => {
    if (!open) {
      setSelectedManagerIds(new Set())
      return
    }
    setSelectedManagerIds(new Set(address ? managerUserIds : []))
    // `managerUserIds` is a fresh array each render; join it so the effect keys on the
    // VALUE, not the identity, and does not fight the user's ticks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, address?.id, managerUserIds.join(',')])

  // Autofill the structured fields + geocoding from a selected Places suggestion.
  const applyParsed = (p: ParsedAddress) =>
    setForm(prev => ({
      ...prev,
      address_line: p.address_line || prev.address_line,
      city: p.city || prev.city,
      state: p.state ?? prev.state,
      country: p.country || prev.country,
      postal_code: p.postal_code || prev.postal_code,
      latitude: p.latitude,
      longitude: p.longitude,
      place_id: p.place_id,
      formatted_address: p.formatted_address,
    }))

  const isValid =
    form.label.trim() &&
    form.country.trim() &&
    form.city.trim() &&
    form.postal_code.trim() &&
    form.address_line.trim()

  // Saving spans two requests now (the address, then its managers), so the button has to
  // stay in its loading state across both — otherwise it goes idle between them and
  // invites a second click.
  const isBusy = isSaving || isSavingManagers

  const handleClose = () => onClose()

  const handleSave = async () => {
    if (!isValid) return
    const payload = {
      type: form.type,
      label: form.label.trim(),
      // Icon only applies to the Custom type; clear it for store/production.
      icon: form.type === 'other' ? form.icon : null,
      // Contact (formerly "Full name") is removed from the modal for now; full_name is
      // still required by the API, so bridge it to the label (keep an existing value on edit).
      full_name: form.full_name.trim() || form.label.trim(),
      country: form.country,
      state: form.state.trim() ? form.state.trim() : null,
      city: form.city.trim(),
      postal_code: form.postal_code.trim(),
      address_line: form.address_line.trim(),
      latitude: form.latitude,
      longitude: form.longitude,
      place_id: form.place_id,
      formatted_address: form.formatted_address,
      is_default: form.is_default,
      show_in_provenance: form.show_in_provenance,
    }
    try {
      let addressId: string
      if (isEdit && address) {
        await update({ id: address.id, payload })
        addressId = address.id
        notification({ type: 'success', title: 'Address updated' })
      } else {
        const created = await create(payload)
        addressId = created.id
      }

      /*
        VRG-565 — managers are a SEPARATE resource with a separate permission, so this is
        a second request rather than a field on the address payload. It runs after the
        address exists, which is what lets the picker work in create mode too: a new
        location can be given its managers in one pass instead of "save, reopen, assign".

        Skipped when nothing changed, so an ordinary address edit by someone WITHOUT
        `address_edit` on the managers route does not 403 on a set they never touched.
      */
      const desired = Array.from(selectedManagerIds)
      const unchanged =
        isEdit &&
        desired.length === managerUserIds.length &&
        desired.every(id => managerUserIds.includes(id))

      if (!unchanged) {
        await setManagers({ addressId, userIds: desired })
      }

      refetch()
      handleClose()
    } catch (error) {
      notification({ type: 'error', title: getErrorMessage(error) || 'Could not save address' })
    }
  }

  return (
    <Modal
      title={isEdit ? 'Edit address' : 'Add new address'}
      open={open}
      onClose={handleClose}
      actions={
        <>
          {/* Cancel stays enabled while saving is in flight? No — cancelling mid-submit
              races the mutation, which is the one legitimate reason to disable it (§54). */}
          <Button disableElevation variant="secondary" onClick={handleClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button disableElevation loading={isBusy} disabled={!isValid} onClick={handleSave}>
            {isEdit ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Stack spacing={3}>
          <Typography variant="body3">Type</Typography>
          {/*
            WRAPS. A modal must never scroll horizontally (§74) — its width is fixed
            by the dialog, so a row that outgrows it has nowhere to go and the
            overflow is invisible until someone notices content is missing.

            This row was fine at four chips and broke at five when `distributor` was
            added, which is the general shape of the problem: a fixed-width container
            holding a list that grows. `rowGap` matches the `spacing` so wrapped rows
            sit on the same rhythm as the columns — `spacing` alone only sets the
            horizontal gap once `flexWrap` is on.
          */}
          <Stack
            direction="row"
            /*
              `gap`, NOT `spacing`. MUI's Stack implements `spacing` as a margin on
              every child except the first, so once the row wraps, the item that
              STARTS the second row is still not `:first-of-type` and keeps its left
              margin — measured: row-1 chips began at x=1006 and the wrapped "Custom"
              at x=1014, an 8px indent that reads as a misalignment because it is one.
              Flex `gap` is applied between items rather than to them, so a wrapped row
              starts flush.
            */
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}
          >
            {ADDRESS_TYPE_OPTIONS.map(option => {
              const active = form.type === option.value
              // The Custom chip reflects the user's chosen icon once picked.
              let optionIcon = option.icon
              if (option.value === 'other' && form.icon) {
                const ChosenIcon = getAddressIcon('other', form.icon)
                optionIcon = <ChosenIcon strokeWidth={1} size={20} />
              }
              return (
                <Stack
                  key={option.value}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  onClick={() => setType(option.value)}
                  sx={{
                    px: 3,
                    py: 2,
                    cursor: 'pointer',
                    borderRadius: 2,
                    border: '1px solid',
                    // Selection is signalled by the brand border + tint background;
                    // the text/icon stay text.primary (white in dark) so they're
                    // readable — matches the Features / activation-settings pattern.
                    // (brand[500] text was too low-contrast on the tint.)
                    borderColor: active ? theme.palette.brand[500] : theme.palette.divider,
                    backgroundColor: active ? alpha(theme.palette.brand[500], 0.12) : 'transparent',
                    color: theme.palette.text.primary,
                    transition: 'all 150ms ease',
                    '&:hover': {
                      borderColor: theme.palette.brand[500],
                      backgroundColor: active
                        ? alpha(theme.palette.brand[500], 0.16)
                        : alpha(theme.palette.brand[500], 0.06),
                    },
                  }}
                >
                  {optionIcon}
                  <Typography variant="body3">{option.label}</Typography>
                </Stack>
              )
            })}
          </Stack>

          {form.type === 'other' && (
            <Stack spacing={2}>
              <Typography variant="body3">Icon</Typography>
              <AddressIconPicker
                value={form.icon}
                onChange={key => set('icon', key)}
              />
            </Stack>
          )}
        </Stack>

        {/* Label — right after Type/Icon (once the icon is chosen), before Address. */}
        <Field label="Label">
          <Input placeholder="Enter" value={form.label} onValueChange={v => set('label', v)} />
        </Field>

        <Field label="Address">
          {/*
            REMOUNTED per open, and seeded from the RECORD rather than from form state.
            Both halves are needed; either alone leaves the field wrong.

            `AddressField` keeps its own `inputValue`, seeded once at mount from
            `defaultValue` (it is an uncontrolled field by design — Places writes to it
            as you type). This modal is mounted ONCE and has the record swapped
            underneath it, so the field never re-seeded: the first open showed it empty,
            every open after that showed the PREVIOUS address, and reopening "Add new"
            appended to what was already there — "1 Test Store Way2 Test Office Way".

            The key includes `open`, not just the id, because two consecutive "Add new"
            opens share an id of `new` and would otherwise not remount — which is the
            case that produced the appending.

            Seeded from `address`, NOT `form.address_line`, because the form is
            populated by an effect that runs AFTER this commit. Keying alone would
            remount the field against the previous record's form state and reintroduce
            the same staleness one render earlier.
          */}
          <AddressField
            key={`${open ? 'open' : 'closed'}:${address?.id ?? 'new'}`}
            apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
            defaultValue={address?.address_line ?? ''}
            onSelect={applyParsed}
            onInputChange={v => set('address_line', v)}
          />
          <Typography variant="body4" color="text.secondary">
            Start typing your address — we&apos;ll fill in the rest. Every field stays editable.
          </Typography>
        </Field>

        <Stack direction="row" spacing={4} alignItems="flex-start">
          <Field label="Country">
            <SelectCountryField value={form.country} onChange={code => set('country', code)} />
          </Field>
          <Field label="State">
            <Input placeholder="Enter" value={form.state} onValueChange={v => set('state', v)} />
          </Field>
        </Stack>

        <Stack direction="row" spacing={4} alignItems="flex-start">
          <Field label="City">
            <Input placeholder="Enter" value={form.city} onValueChange={v => set('city', v)} />
          </Field>
          <Field label="Postal code">
            <Input placeholder="Enter" value={form.postal_code} onValueChange={v => set('postal_code', v)} />
          </Field>
        </Stack>

        {/*
          VRG-565 — who manages this location.

          It lives HERE, on the address, because that is the direction the question is
          actually asked in: a location has staff. The mirror-image placement — a
          "locations this person covers" picker on the Users page — is the same edge read
          the other way and is NOT built; see the ticket.

          §67: the effect of this field lands somewhere else entirely (the VERA app, when
          a manager confirms an arrival), so it owes a tooltip naming where that is.
        */}
        <Field
          label="Location managers"
          optional
          adornment={
            <InfoTooltip
              doc="company-settings/settings-address-book"
              text="The people who can confirm that items have arrived at this location. It takes effect in the VERA app, not on this page. Everyone else in your company is unaffected."
            />
          }
        >
          {/*
            COLLAPSED, not the inline `UserChecklist` this used to render (VRG-528).

            The checklist is the right control when the roster IS the surface (the
            dedicated Assign modal). As one field among nine it was the tallest thing in
            the dialog — an always-open search box plus five visible rows — for a field
            most brands never touch. Measured before/after on Edit at 1280x800: the form's
            natural height fell from 1124px to 871px — a 253px saving, and the single
            largest field in the dialog became a 49px one.

            ⚠️ The RENDERED dialog is 736px in both cases, and that is not the fix
            failing: 736 is `Modal`'s own `maxHeight: calc(100vh - 64px)` at an 800px
            viewport, so the dialog is clamped either way and the honest measure is the
            unclamped content height. 871 still exceeds the cap, so the modal continues to
            scroll on a laptop; it now needs 135px of scroll instead of 388px.

            `UserChecklist` is NOT deleted: it still has its other callers and is the
            right pattern at its own altitude. See `UserSelectField`'s own note.
          */}
          <UserSelectField
            users={companyUsersData?.data ?? []}
            selectedIds={selectedManagerIds}
            onChange={setManagerIds}
            isLoading={isLoadingUsers || isLoadingManagers}
            placeholder="Select people"
            emptyMessage="No people in your company yet."
          />
        </Field>

        <Divider />

        <FormControlLabel
          sx={{ ml: 0 }}
          control={
            <Checkbox
              size="small"
              sx={{ p: 0, mr: 2 }}
              checked={form.is_default}
              onChange={(_, checked) => set('is_default', checked)}
            />
          }
          label={<Typography variant="body3">Save as default address</Typography>}
        />

        {/*
          VERA-931 — publication is a SEPARATE question from where stock is sent.
          Any address can receive items and trigger activation; only some should be
          NAMED to customers. A shop is the point of a chain of custody ("bought at
          Bond Street"); a contract manufacturer usually is not something a brand
          wants on a page anyone can open. Defaulted from the type on load, so the
          box shows what actually happens rather than an unset field.

          "as part of provenance" in the label is doing real work, not padding. The old
          wording — "Show this location on public certificates" — did not say WHERE it
          shows, and a brand could reasonably read it as putting their shop address on
          the page as contact information. What it actually does is add the location as
          a link in the chain of custody. Naming the place is what lets someone decide,
          which is the whole point of the setting existing separately from the billing
          trigger. Kept as a statement rather than a question: a checkbox label describes
          the state being switched on, and a question reads as if the box were asking
          rather than setting.

          ⚠️ This note lives OUT HERE. It was written inside `label={…}` as a braced JSX
          comment, and `label={` opens a JS EXPRESSION — where a braced comment parses as
          an empty object literal, immediately followed by a `<Typography>` with no
          operator between them. That is a hard syntax error, and it took out the whole
          Vite bundle rather than just this modal: every platform route served a babel
          overlay until it was removed.
        */}
        <FormControlLabel
          sx={{ ml: 0 }}
          control={
            <Checkbox
              size="small"
              sx={{ p: 0, mr: 2 }}
              checked={form.show_in_provenance}
              onChange={(_, checked) => set('show_in_provenance', checked)}
            />
          }
          label={
            <Typography variant="body3">
              Show this location as part of provenance on public certificates
            </Typography>
          }
        />
      </Stack>
    </Modal>
  )
}
